import React, { useState, useEffect } from 'react';
import { locationAPI, Location, LocationInput } from '../services/locationAPI';

interface LocationManagerProps {
  /** Fired after a location is created, edited or deleted, so a parent that also
   *  lists locations (e.g. the Add Property dropdown) can refresh immediately
   *  instead of only on remount. */
  onLocationsChange?: () => void;
}

const LocationManager: React.FC<LocationManagerProps> = ({ onLocationsChange }) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showCopyForm, setShowCopyForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [copyingLocation, setCopyingLocation] = useState<Location | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<LocationInput>({
    name: '',
    adr_jan: 155,
    adr_feb: 160,
    adr_mar: 175,
    adr_apr: 190,
    adr_may: 320,
    adr_jun: 350,
    adr_jul: 320,
    adr_aug: 300,
    adr_sep: 190,
    adr_oct: 175,
    adr_nov: 160,
    adr_dec: 155,
    occupancy_jan: 45.0,
    occupancy_feb: 48.0,
    occupancy_mar: 50.0,
    occupancy_apr: 55.0,
    occupancy_may: 75.0,
    occupancy_jun: 80.0,
    occupancy_jul: 82.0,
    occupancy_aug: 75.0,
    occupancy_sep: 60.0,
    occupancy_oct: 55.0,
    occupancy_nov: 50.0,
    occupancy_dec: 48.0,
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const data = await locationAPI.getLocations();
      setLocations(data);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof LocationInput, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      if (editingLocation) {
        await locationAPI.updateLocation(editingLocation.id, formData);
        setShowEditForm(false);
        setEditingLocation(null);
      } else if (copyingLocation) {
        await locationAPI.createLocation(formData);
        setShowCopyForm(false);
        setCopyingLocation(null);
      } else {
        await locationAPI.createLocation(formData);
        setShowAddForm(false);
      }

      // Reset form data
      setFormData({
        name: '',
        adr_jan: 155,
        adr_feb: 160,
        adr_mar: 175,
        adr_apr: 190,
        adr_may: 320,
        adr_jun: 350,
        adr_jul: 320,
        adr_aug: 300,
        adr_sep: 190,
        adr_oct: 175,
        adr_nov: 160,
        adr_dec: 155,
        occupancy_jan: 45.0,
        occupancy_feb: 48.0,
        occupancy_mar: 50.0,
        occupancy_apr: 55.0,
        occupancy_may: 75.0,
        occupancy_jun: 80.0,
        occupancy_jul: 82.0,
        occupancy_aug: 75.0,
        occupancy_sep: 60.0,
        occupancy_oct: 55.0,
        occupancy_nov: 50.0,
        occupancy_dec: 48.0,
      });

      await fetchLocations();
      onLocationsChange?.();
    } catch (err: any) {
      console.error('Error in handleSubmit:', err);
      setError(err.message || 'Failed to save location');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (location: Location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      adr_jan: location.adr_jan,
      adr_feb: location.adr_feb,
      adr_mar: location.adr_mar,
      adr_apr: location.adr_apr,
      adr_may: location.adr_may,
      adr_jun: location.adr_jun,
      adr_jul: location.adr_jul,
      adr_aug: location.adr_aug,
      adr_sep: location.adr_sep,
      adr_oct: location.adr_oct,
      adr_nov: location.adr_nov,
      adr_dec: location.adr_dec,
      occupancy_jan: location.occupancy_jan,
      occupancy_feb: location.occupancy_feb,
      occupancy_mar: location.occupancy_mar,
      occupancy_apr: location.occupancy_apr,
      occupancy_may: location.occupancy_may,
      occupancy_jun: location.occupancy_jun,
      occupancy_jul: location.occupancy_jul,
      occupancy_aug: location.occupancy_aug,
      occupancy_sep: location.occupancy_sep,
      occupancy_oct: location.occupancy_oct,
      occupancy_nov: location.occupancy_nov,
      occupancy_dec: location.occupancy_dec,
    });
    setShowEditForm(true);
  };

  const handleCopy = (location: Location) => {
    setCopyingLocation(location);
    setFormData({
      name: `${location.name} (Copy)`,
      adr_jan: location.adr_jan,
      adr_feb: location.adr_feb,
      adr_mar: location.adr_mar,
      adr_apr: location.adr_apr,
      adr_may: location.adr_may,
      adr_jun: location.adr_jun,
      adr_jul: location.adr_jul,
      adr_aug: location.adr_aug,
      adr_sep: location.adr_sep,
      adr_oct: location.adr_oct,
      adr_nov: location.adr_nov,
      adr_dec: location.adr_dec,
      occupancy_jan: location.occupancy_jan,
      occupancy_feb: location.occupancy_feb,
      occupancy_mar: location.occupancy_mar,
      occupancy_apr: location.occupancy_apr,
      occupancy_may: location.occupancy_may,
      occupancy_jun: location.occupancy_jun,
      occupancy_jul: location.occupancy_jul,
      occupancy_aug: location.occupancy_aug,
      occupancy_sep: location.occupancy_sep,
      occupancy_oct: location.occupancy_oct,
      occupancy_nov: location.occupancy_nov,
      occupancy_dec: location.occupancy_dec,
    });
    setShowCopyForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this location?')) {
      return;
    }

    try {
      setLoading(true);
      await locationAPI.deleteLocation(id);
      await fetchLocations();
      onLocationsChange?.();
    } catch (err: any) {
      setError(err.message || 'Failed to delete location');
    } finally {
      setLoading(false);
    }
  };



  const renderMonthlyInputs = (prefix: 'adr' | 'occupancy', label: string) => (
    <div className="mb-6">
      <h4 className="text-lg font-semibold text-gray-800 mb-3">{label}</h4>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].map(month => (
          <div key={month}>
            <label className="block text-xs font-medium text-gray-600 mb-1 capitalize">
              {month}
            </label>
            <input
              type="number"
              value={formData[`${prefix}_${month}` as keyof LocationInput] || ''}
              onChange={(e) => handleInputChange(`${prefix}_${month}` as keyof LocationInput, parseFloat(e.target.value) || 0)}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-vista-teal-500"
              step={prefix === 'occupancy' ? 0.1 : 1}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const renderForm = (title: string, submitText: string) => (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">{title}</h2>
      
      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Location Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500"
            placeholder="e.g., Cannes City Center, Antibes Beach"
            required
          />
        </div>

        {/* Monthly Base ADR */}
        {renderMonthlyInputs('adr', 'Monthly Base ADR (€)')}

        {/* Monthly Base Occupancy */}
        {renderMonthlyInputs('occupancy', 'Monthly Base Occupancy (%)')}

        <div className="flex gap-4">
          <button
            type="submit"
            disabled={loading}
            className="bg-vista-teal-600 text-white px-6 py-2 rounded-md hover:bg-vista-teal-700 transition-colors disabled:opacity-50"
          >
            {loading ? 'Saving...' : submitText}
          </button>
          <button
            type="button"
            onClick={() => {
              setShowAddForm(false);
              setShowEditForm(false);
              setShowCopyForm(false);
              setEditingLocation(null);
              setCopyingLocation(null);
            }}
            className="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <div className="mb-8">
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold text-gray-800">Location Management</h2>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-vista-teal-600 text-white px-4 py-2 rounded-md hover:bg-vista-teal-700 transition-colors"
          >
            Add New Location
          </button>
        </div>
        


        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {loading && !showAddForm && !showEditForm && !showCopyForm && (
          <div className="text-center py-4">Loading locations...</div>
        )}

        {!loading && locations.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No locations found. Create your first location to get started.
          </div>
        )}

        {locations.length > 0 && (
          <div className="grid gap-4">
            {locations.map((location) => (
              <div key={location.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-semibold text-gray-800">{location.name}</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(location)}
                      className="bg-vista-teal-600 text-white px-3 py-1 rounded text-sm hover:bg-vista-teal-700 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleCopy(location)}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                    >
                      Copy
                    </button>
                    <button
                      onClick={() => handleDelete(location.id)}
                      className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">ADR Range:</span> €{Math.min(...Object.values(location).filter(v => typeof v === 'number' && v > 0 && v < 1000))} - €{Math.max(...Object.values(location).filter(v => typeof v === 'number' && v > 0 && v < 1000))}
                  </div>
                  <div>
                    <span className="font-medium">Occupancy Range:</span> {Math.min(...Object.values(location).filter(v => typeof v === 'number' && v > 0 && v < 100))}% - {Math.max(...Object.values(location).filter(v => typeof v === 'number' && v > 0 && v < 100))}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Form */}
      {showAddForm && renderForm('Add New Location', 'Create Location')}

      {/* Edit Form */}
      {showEditForm && editingLocation && renderForm('Edit Location', 'Update Location')}

      {/* Copy Form */}
      {showCopyForm && copyingLocation && renderForm('Copy Location', 'Create Copy')}
    </div>
  );
};

export default LocationManager;
